import { Link } from 'react-router-dom';
import FlowFieldBackground from './landing/FlowFieldBackground';
import Logo from './Logo';

export default function Footer({ embedded = false }) {
  const year = new Date().getFullYear();

  const companyLinks = [
    { label: 'About', to: '/about' },
    { label: 'Blog', to: '/blog' },
    { label: 'Free Risk Report', to: '/schedule-demo' },
    { label: 'Contact', to: '/contact' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy',   to: '/privacy' },
    { label: 'Terms of Service', to: '/terms' },
  ];

  return (
    <footer
      className={`theme-band${embedded ? ' cta-footer-bridge__footer' : ' footer-with-flow'}`}
      style={{ position: 'relative' }}
    >
      {!embedded && (
        <FlowFieldBackground
          gradientId="footer-flow-grad"
          preset="hero"
          fadeIn={false}
          uniformStroke
        />
      )}

      <div aria-hidden="true" className="footer-flow-legibility" />

      <div className="max-w-7xl mx-auto px-8 lg:px-12 py-20 lg:py-24 relative z-[1]">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 lg:gap-16 mb-16 lg:mb-20">

          {/* Brand — 2 cols */}
          <div className="lg:col-span-2">
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', marginBottom: '1.5rem', textDecoration: 'none' }}>
              <Logo variant="auto" style={{ height: 28, width: 'auto' }} />
            </Link>

            <p className="footer-tagline" style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.9375rem',
              lineHeight: 1.72,
              maxWidth: 320,
              letterSpacing: '0.01em',
              ...(embedded ? {} : {
                fontWeight: 300,
                color: 'var(--band-text)',
                opacity: 0.35,
              }),
            }}>
              AI-powered employee retention. Predict turnover. Protect your people.
            </p>
          </div>

          {/* Company links */}
          <div>
            <div className="footer-col-label" style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.5625rem',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              marginBottom: '1.5rem',
              ...(embedded ? {} : {
                fontWeight: 300,
                color: 'var(--band-text-mid)',
                opacity: 0.6,
              }),
            }}>
              Company
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              {companyLinks.map(link => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="footer-nav-link"
                    style={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                      letterSpacing: '0.02em',
                      cursor: 'pointer',
                      display: 'inline-block',
                      ...(embedded ? {} : {
                        fontWeight: 300,
                        color: 'var(--band-text)',
                        opacity: 0.45,
                        transition: 'opacity 0.3s ease',
                      }),
                    }}
                    onMouseEnter={embedded ? undefined : (e => { e.currentTarget.style.opacity = '0.75'; })}
                    onMouseLeave={embedded ? undefined : (e => { e.currentTarget.style.opacity = '0.45'; })}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal links */}
          <div>
            <div className="footer-col-label" style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.5625rem',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              marginBottom: '1.5rem',
              ...(embedded ? {} : {
                fontWeight: 300,
                color: 'var(--band-text-mid)',
                opacity: 0.6,
              }),
            }}>
              Legal
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              {legalLinks.map(link => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="footer-nav-link"
                    style={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: '0.875rem',
                      textDecoration: 'none',
                      letterSpacing: '0.02em',
                      cursor: 'pointer',
                      display: 'inline-block',
                      ...(embedded ? {} : {
                        fontWeight: 300,
                        color: 'var(--band-text)',
                        opacity: 0.45,
                        transition: 'opacity 0.3s ease',
                      }),
                    }}
                    onMouseEnter={embedded ? undefined : (e => { e.currentTarget.style.opacity = '0.75'; })}
                    onMouseLeave={embedded ? undefined : (e => { e.currentTarget.style.opacity = '0.45'; })}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{
          paddingTop: '1.75rem',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <span className="footer-copyright" style={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '0.75rem',
            letterSpacing: '0.04em',
            ...(embedded ? {} : {
              fontWeight: 300,
              color: 'var(--band-text)',
              opacity: 0.25,
            }),
          }}>
            © {year} Stavyn. All rights reserved.
          </span>

          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: '#3A7A4A',
              animation: 'pulse-dot 2.4s ease-in-out infinite',
            }}/>
            <span className="footer-status" style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: '0.6875rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              ...(embedded ? {} : {
                fontWeight: 300,
                color: 'rgba(58,122,74,0.7)',
              }),
            }}>
              All systems operational
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
